import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Lock, Play } from "lucide-react";
import api from "@/lib/api";

const NODE = {
  done: "border-primary bg-primary/15 text-primary shadow-[0_0_14px_hsl(var(--primary)/0.55)]",
  next: "border-secondary bg-secondary/15 text-secondary node-pulse",
  locked: "border-line bg-background text-muted-foreground",
};

export default function Lernpfad() {
  const [courses, setCourses] = useState([]);
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    api
      .get("/courses")
      .then(async (r) => {
        const full = await Promise.all(r.data.map((s) => api.get(`/courses/${s.slug}`).then((res) => res.data)));
        setCourses(full);
      })
      .catch(() => {});
    api.get("/progress").then((r) => setProgress(r.data)).catch(() => {});
  }, []);

  const completed = new Set(progress?.completed_lessons || []);

  return (
    <div className="space-y-8" data-testid="lernpfad-page">
      <div className="animate-fade-up">
        <p className="eyebrow mb-1">// Lernpfad</p>
        <h1 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-foreground">Dein Schaltplan</h1>
        <p className="text-muted-foreground mt-1">Jede abgeschlossene Lektion bestromt die Leiterbahn zur nächsten.</p>
      </div>

      {courses.map((course) => {
        let nextFound = false;
        const states = course.lessons.map((lesson) => {
          if (completed.has(`${course.slug}/${lesson.id}`)) return "done";
          if (!nextFound) {
            nextFound = true;
            return "next";
          }
          return "locked";
        });
        const doneCount = states.filter((s) => s === "done").length;

        return (
          <section key={course.slug} data-testid={`lernpfad-lane-${course.slug}`} className="nb-card p-5 sm:p-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
              <div className="flex items-center gap-2.5">
                <span className="h-3 w-3 rounded-full shrink-0" style={{ backgroundColor: course.color, boxShadow: `0 0 10px ${course.color}` }} />
                <h2 className="font-heading font-bold text-xl text-foreground">{course.title}</h2>
              </div>
              <span className="font-mono text-sm text-muted-foreground">
                {doneCount}/{course.lessons.length} bestromt
              </span>
            </div>

            <ol className="flex items-start overflow-x-auto pb-2" aria-label={`Lektionen in ${course.title}`}>
              {course.lessons.map((lesson, i) => {
                const state = states[i];
                const wireOn = i > 0 && states[i - 1] === "done";
                return (
                  <li key={lesson.id} className="flex items-start shrink-0">
                    {i > 0 && (
                      <span
                        aria-hidden="true"
                        className={`mt-[27px] h-0.5 w-8 sm:w-12 shrink-0 rounded-full ${wireOn ? "trace-on" : "bg-line"}`}
                      />
                    )}
                    <Link
                      to={`/kurs/${course.slug}/lektion/${lesson.id}`}
                      data-testid={`lernpfad-node-${course.slug}-${lesson.id}`}
                      title={lesson.title}
                      aria-label={`${lesson.title} – ${state === "done" ? "abgeschlossen" : state === "next" ? "als Nächstes dran" : "noch offen"}`}
                      className="group flex w-24 shrink-0 flex-col items-center gap-2"
                    >
                      <motion.span
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: i * 0.03 }}
                        className={`flex h-14 w-14 items-center justify-center rounded-full border-2 transition-transform group-hover:scale-105 ${NODE[state]}`}
                      >
                        {state === "done" && <Check className="h-6 w-6" strokeWidth={2.4} />}
                        {state === "next" && <Play className="h-6 w-6" strokeWidth={2.4} />}
                        {state === "locked" && <Lock className="h-5 w-5" strokeWidth={2.2} />}
                      </motion.span>
                      <span
                        className={`text-center text-[12px] leading-tight ${
                          state === "locked" ? "text-muted-foreground" : "font-medium text-foreground"
                        }`}
                      >
                        {lesson.title}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ol>
          </section>
        );
      })}
    </div>
  );
}
